// File: src/components/OmniCine/Toolbar/ImportButton.tsx
//
// ImportButton — Phase M1 strict ingest authority
// ------------------------------------------------------------------------------
// Owns: picker + requestId + previewUrl + immediate timeline:add (preview)
// Helper owns: upload + registry write + optional downloadUrl
// Emits optional second timeline:add to upgrade URL (same requestId)
// Telemetry: window.__oc_media.__lastIngest
//
// ✅ Patch (stuck importing):
// - Add timeout guard around ingest helper
// - Always finalize telemetry stage
// - Ensure busy is released in all paths

import * as React from "react";
import { emitTimelineAddEvent } from "@/contracts/media/MediaPanelEvents";
import { ingestFileToRegistryAndEmitAdd } from "@/lib/media/ingest/ingestFromLocalLibrary";

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

type ImportButtonProps = {
  className?: string;
  storagePrefix?: string;
  trackId?: string;
  label?: string;
  busyLabel?: string;
  title?: string;
};

type LastIngestStage =
  | "idle"
  | "start"
  | "picked"
  | "emit_preview"
  | "uploading"
  | "registry_written"
  | "emit_upgrade"
  | "done"
  | "error"
  | "timeout";

type LastIngest = {
  ok: boolean;
  at: number;
  stage: LastIngestStage;
  note?: string;

  requestId?: string;
  previewUrl?: string;

  fileName?: string;
  mime?: string;
  size?: number;

  key?: string;
  kind?: string;
  storagePath?: string;
  downloadUrl?: string | null;
  durSec?: number | null;

  error?: string;
};

function setLastIngest(patch: Partial<LastIngest>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;
  w.__oc_media = w.__oc_media || {};

  const prev: LastIngest =
    (w.__oc_media.__lastIngest as LastIngest | undefined) || {
      ok: false,
      at: Date.now(),
      stage: "idle",
    };

  const next: LastIngest = {
    ...prev,
    ...patch,
    at: patch.at ?? Date.now(),
  };

  w.__oc_media.__lastIngest = next;
  return next;
}

function makeRequestId(): string {
  return (
    (globalThis.crypto?.randomUUID?.() as string | undefined) ??
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function readStr(v: unknown): string {
  return typeof v === "string" && v.trim() ? v.trim() : "";
}

function dispatchTimelineAdd(detail: Record<string, unknown>) {
  emitTimelineAddEvent(detail);
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  const t = Math.max(500, ms);
  return new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error(`ingest timeout after ${t}ms`)), t);
    p.then(
      (v) => {
        window.clearTimeout(id);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(id);
        reject(e);
      },
    );
  });
}

async function readLocalMediaDurationSec(
  url: string,
  kind: "audio" | "video" | "image",
): Promise<number | null> {
  if (!url || kind === "image") return null;

  return await new Promise<number | null>((resolve) => {
    const el = document.createElement(kind === "audio" ? "audio" : "video");
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("error", onError);
      el.removeAttribute("src");
      try {
        el.load();
      } catch {
        // ignore
      }
    };
    const onLoadedMetadata = () => {
      const durationSec = Number(el.duration);
      cleanup();
      resolve(Number.isFinite(durationSec) && durationSec > 0 ? durationSec : null);
    };
    const onError = () => {
      cleanup();
      resolve(null);
    };
    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, 4000);

    el.preload = "metadata";
    el.src = url;
    el.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
    el.addEventListener("error", onError, { once: true });

    try {
      el.load();
    } catch {
      cleanup();
      resolve(null);
    }
  });
}

export default function ImportButton(props: ImportButtonProps): JSX.Element {
  const storagePrefix = props.storagePrefix ?? "omnicine/library";
  const trackIdHint = readStr(props.trackId);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState(false);

  // Revoke preview URLs we created (avoid leaks)
  const previewUrlsRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    return () => {
      for (const u of previewUrlsRef.current) {
        try {
          URL.revokeObjectURL(u);
        } catch {
          // ignore
        }
      }
      previewUrlsRef.current = [];
    };
  }, []);

  const pickFile = React.useCallback(async (): Promise<File | null> => {
    const input = inputRef.current;
    if (!input) return null;

    return await new Promise<File | null>((resolve) => {
      const onChange = () => {
        input.removeEventListener("change", onChange);
        const f = input.files?.[0] ?? null;
        input.value = ""; // allow re-pick same file
        resolve(f);
      };

      input.addEventListener("change", onChange, { once: true });
      input.click();
    });
  }, []);

  const onClick = React.useCallback(async () => {
    if (busy) return;

    const requestId = makeRequestId();
    let previewUrl = "";

    setBusy(true);
    setLastIngest({ ok: false, stage: "start", note: "Import clicked", requestId });

    try {
      // 1) pick file
      const file = await pickFile();
      if (!file) {
        setLastIngest({
          ok: false,
          stage: "idle",
          note: "Picker cancelled / no file selected",
          requestId,
        });
        return;
      }

      const mime = file.type || "application/octet-stream";

      setLastIngest({
        ok: false,
        stage: "picked",
        note: "File selected",
        requestId,
        fileName: file.name,
        mime,
        size: file.size,
      });

      // 2) preview url
      previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.push(previewUrl);

      const kind = mime.startsWith("audio/")
        ? "audio"
        : mime.startsWith("video/")
          ? "video"
          : mime.startsWith("image/")
            ? "image"
            : "video";

      // 3) emit preview immediately
      setLastIngest({
        ok: false,
        stage: "emit_preview",
        note: "Emitting timeline:add (preview)",
        requestId,
        previewUrl,
      });

      dispatchTimelineAdd({
        requestId,
        kind,
        trackId: trackIdHint || undefined,
        startSec: 0,
        url: previewUrl,
        previewUrl,
        fileName: file.name,
        sourceAssetId: requestId,
        originType: kind === "audio" ? "audioOnly" : kind === "image" ? "videoOnly" : "videoWithEmbeddedAudio",
        hasPlayableAudio: kind === "video" ? true : kind === "image" ? false : undefined,
        mediaId: undefined,
        source: "ImportButton:preview",
      });

      const previewDurationSec = await readLocalMediaDurationSec(previewUrl, kind);
      if (typeof previewDurationSec === "number" && Number.isFinite(previewDurationSec) && previewDurationSec > 0) {
        dispatchTimelineAdd({
          requestId,
          kind,
          trackId: trackIdHint || undefined,
          startSec: 0,
          endSec: previewDurationSec,
          url: previewUrl,
          previewUrl,
          durSec: previewDurationSec,
          fileName: file.name,
          sourceAssetId: requestId,
          originType: kind === "audio" ? "audioOnly" : kind === "image" ? "videoOnly" : "videoWithEmbeddedAudio",
          hasPlayableAudio: kind === "video" ? true : kind === "image" ? false : undefined,
          mediaId: undefined,
          source: "ImportButton:preview-duration",
        });
      }

      // 4) ingest (timeout guarded)
      setLastIngest({
        ok: false,
        stage: "uploading",
        note: "Uploading / writing registry",
        requestId,
        previewUrl,
      });

      const res = await withTimeout(
        ingestFileToRegistryAndEmitAdd({
          storagePrefix,
          requestId,
          file,
        }),
        45_000, // ✅ hard guard to prevent “Importing…” forever
      );

      if (!res) {
        setLastIngest({
          ok: false,
          stage: "error",
          note: "Ingest returned null",
          requestId,
          previewUrl,
          error: "Ingest returned null",
        });
        return;
      }

      setLastIngest({
        ok: true,
        stage: "registry_written",
        note: "Registry written",
        requestId,
        previewUrl,
        key: res.key,
        kind: res.kind,
        storagePath: res.storagePath,
        downloadUrl: res.downloadUrl ?? null,
        durSec: res.durSec ?? null,
        fileName: res.fileName ?? file.name,
        mime: res.mime ?? mime,
        size: file.size,
      });

      // 5) upgrade url if we have it
      if (readStr(res.downloadUrl)) {
        setLastIngest({
          ok: true,
          stage: "emit_upgrade",
          note: "Emitting timeline:add (upgrade url)",
          requestId,
          previewUrl,
          key: res.key,
          downloadUrl: res.downloadUrl ?? null,
        });

        dispatchTimelineAdd({
          requestId,
          kind: res.kind ?? kind,
          trackId: trackIdHint || undefined,
          startSec: 0,
          endSec: typeof res.durSec === "number" && Number.isFinite(res.durSec) && res.durSec > 0 ? res.durSec : undefined,
          mediaId: res.key,
          url: res.downloadUrl,
          previewUrl,
          durSec: res.durSec ?? undefined,
          fileName: res.fileName ?? file.name,
          sourceAssetId: res.key,
          originType:
            (res.kind ?? kind) === "audio"
              ? "audioOnly"
              : (res.kind ?? kind) === "image"
                ? "videoOnly"
                : "videoWithEmbeddedAudio",
          hasPlayableAudio: (res.kind ?? kind) === "video" ? true : (res.kind ?? kind) === "image" ? false : undefined,
          storagePath: res.storagePath,
          source: "ImportButton:upgrade",
        });
      }

      setLastIngest({
        ok: true,
        stage: "done",
        note: "Ingest complete",
        requestId,
        previewUrl,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);

      setLastIngest({
        ok: false,
        stage: msg.includes("timeout") ? "timeout" : "error",
        error: msg,
        note: "Ingest failed",
        requestId,
        previewUrl: previewUrl || undefined,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = window as any;
      w.__oc_media = w.__oc_media || {};
      w.__oc_media.__lastIngestError = { at: Date.now(), requestId, error: msg };

      // eslint-disable-next-line no-console
      console.error("[ImportButton] ingest failed:", err);
    } finally {
      setBusy(false);
    }
  }, [busy, pickFile, storagePrefix, trackIdHint]);

  return (
    <>
      <input ref={inputRef} type="file" className="hidden" accept="audio/*,video/*" />

      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className={cx(
          props.className ||
            "rounded-xl px-3 py-2 text-sm border border-white/15 bg-white/10 hover:bg-white/15 text-white/90",
          busy && "opacity-60 cursor-not-allowed",
        )}
        title={
          props.title ?? "Import audio/video from your computer, add to timeline immediately, then persist to registry"
        }
      >
        {busy ? props.busyLabel ?? "Importing…" : props.label ?? "Import"}
      </button>
    </>
  );
}
